// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useState } from 'react';

import { ApiRequestError, api } from '../../api/client';
import { parseRuxp } from '../../viewport/binaryPoints';

/** What Snit reads of the scan: positions (world, stride 3) and colours (stride 3), a level-of-detail sample. */
export interface SectionCloud {
  xyz: Float32Array;
  rgb: Uint8Array | null;
  /** Points in the whole cloud (the sample holds `xyz.length / 3`). */
  total: number;
}

/** The sample's budget: enough for a 30 cm band to read as walls and floors. */
const BINARY_POINTS = 400_000;
/** A server without the binary transport answers JSON; a smaller budget keeps it quick. */
const JSON_POINTS = 80_000;

const cache = new Map<string, Promise<SectionCloud | null>>();

async function load(): Promise<SectionCloud | null> {
  try {
    const page = parseRuxp(await api.cloudPointsBinary('cloud', { maxPoints: BINARY_POINTS }));
    const xyz = page.view('xyz');
    const rgb = page.view('rgb');
    if (!(xyz instanceof Float32Array)) return null;
    return { xyz, rgb: rgb instanceof Uint8Array ? rgb : null, total: page.total };
  } catch (cause) {
    // No cloud at all; anything else (an older server's 501 first) tries the JSON transport.
    if (cause instanceof ApiRequestError && cause.isNotFound) return null;
  }
  try {
    const page = await api.cloudPoints('cloud', { maxPoints: JSON_POINTS });
    const [ix, iy, iz] = ['x', 'y', 'z'].map((f) => page.fields.indexOf(f));
    if (ix < 0 || iy < 0 || iz < 0) return null;
    const [ir, ig, ib] = ['r', 'g', 'b'].map((f) => page.fields.indexOf(f));
    const xyz = new Float32Array(page.points.length * 3);
    const rgb = ir >= 0 && ig >= 0 && ib >= 0 ? new Uint8Array(page.points.length * 3) : null;
    page.points.forEach((row, i) => {
      xyz[i * 3] = row[ix];
      xyz[i * 3 + 1] = row[iy];
      xyz[i * 3 + 2] = row[iz];
      if (rgb) {
        rgb[i * 3] = row[ir];
        rgb[i * 3 + 1] = row[ig];
        rgb[i * 3 + 2] = row[ib];
      }
    });
    return { xyz, rgb, total: page.total };
  } catch {
    return null;
  }
}

/**
 * The scan as Snit draws it under the cut and its height histogram reads
 * it: one level-of-detail sample of the `cloud` per case, fetched the first
 * time Snit opens and kept for the session (null while it loads, or when
 * the case has no cloud).
 */
export function useSectionCloud(enabled: boolean): { cloud: SectionCloud | null; loading: boolean } {
  const key = api.caseId ?? 'lokal';
  const [state, setState] = useState<{ key: string; cloud: SectionCloud | null; loading: boolean }>({ key, cloud: null, loading: false });
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    let p = cache.get(key);
    if (!p) {
      p = load();
      cache.set(key, p);
      // A failed read is not kept: the next opening asks again.
      void p.then((c) => {
        if (c === null) cache.delete(key);
      });
    }
    setState((s) => (s.key === key && s.cloud ? s : { key, cloud: null, loading: true }));
    void p.then((cloud) => {
      if (alive) setState({ key, cloud, loading: false });
    });
    return () => {
      alive = false;
    };
  }, [enabled, key]);
  return state.key === key ? { cloud: state.cloud, loading: state.loading } : { cloud: null, loading: enabled };
}
